/**
 * Step Tracker — Reliability & Offline Trust (MVP)
 * -----------------------------------------------------------------------------
 * Why this rewrite (2026-05-23):
 *   The previous implementation showed a vague "Syncing..." string and
 *   leaned on a fabricated-data service that invented step counts via
 *   setInterval. That eroded trust. This version is local-first, offline
 *   aware, and renders explicit status copy for every state the user can
 *   actually be in. No fake data, no spinner that never resolves, no
 *   misleading "live sync" language.
 *
 * Surface:
 *   - Mint-green HealthLoop palette throughout (replaces blue/purple).
 *   - Status pill maps 1:1 to STATUS enum from deviceHealthService.
 *   - "Last synced N mins ago" relative-time line (auto-updates each minute).
 *   - Offline banner uses navigator.onLine + 'online'/'offline' events; we
 *     keep tracking locally and queue backend updates until reconnect.
 *   - Empty state (steps === 0) shows reassuring guidance, not a broken zero.
 *   - Manual refresh button shows loading → "Updated successfully" toast.
 *   - Trust line: "Your wellness data stays on your device unless synced."
 *   - On mount we render the cached value instantly (no spinner flash),
 *     then attempt a backend sync.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  ArrowLeft,
  TrendingUp,
  Target,
  Award,
  Smartphone,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Activity,
  Footprints,
  WifiOff,
  Lock,
  ShieldCheck,
  Clock,
  PauseCircle,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import Confetti from 'react-confetti';
import deviceHealthService, { STATUS } from '@/services/deviceHealthService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const PENDING_RETRY_KEY = 'healthloop.steps.lastBackendFailureAt';

/**
 * Compact relative-time formatter — kept local because the rest of the app
 * doesn't have one yet and it's 8 lines. "just now / 2 mins ago / 1 hour ago".
 */
function formatRelative(date) {
  if (!date) return null;
  const ms = Date.now() - new Date(date).getTime();
  if (ms < 30 * 1000) return 'just now';
  const mins = Math.floor(ms / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins} min${mins === 1 ? '' : 's'} ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`;
  const days = Math.floor(hrs / 24);
  return `${days} day${days === 1 ? '' : 's'} ago`;
}

/**
 * Maps the explicit STATUS enum to the UI presentation. Centralising it here
 * means new statuses only need a row added below — no scattered if/else.
 */
const STATUS_PRESENTATION = {
  [STATUS.UNAVAILABLE]: {
    label: 'Manual tracking only',
    sub: 'No connected health source on this device.',
    tone: 'neutral',
    Icon: Smartphone,
  },
  [STATUS.PERMISSION_NEEDED]: {
    label: 'Waiting for permission',
    sub: 'Connect to read step data from your device.',
    tone: 'warning',
    Icon: Lock,
  },
  [STATUS.PERMISSION_DENIED]: {
    label: 'Health permissions required',
    sub: 'Enable access in settings, or keep logging manually below.',
    tone: 'warning',
    Icon: Lock,
  },
  [STATUS.CONNECTED_WAITING]: {
    label: 'Tracking active',
    sub: 'Waiting for activity data — take a short walk to test tracking.',
    tone: 'info',
    Icon: Activity,
  },
  [STATUS.CONNECTED_ACTIVE]: {
    label: 'Connected',
    sub: 'Tracking active.',
    tone: 'success',
    Icon: CheckCircle2,
  },
  [STATUS.OFFLINE]: {
    label: 'Offline mode active',
    sub: 'Tracking continues offline. Updates will sync when you reconnect.',
    tone: 'offline',
    Icon: WifiOff,
  },
  [STATUS.SYNC_PAUSED]: {
    label: 'Sync paused',
    sub: 'Local tracking is unaffected. Tap refresh to try again.',
    tone: 'warning',
    Icon: PauseCircle,
  },
};

const TONE_CLASSES = {
  success: 'border-emerald-200 bg-emerald-50 text-emerald-900',
  info: 'border-teal-200 bg-teal-50 text-teal-900',
  warning: 'border-amber-200 bg-amber-50 text-amber-900',
  offline: 'border-slate-200 bg-slate-50 text-slate-800',
  neutral: 'border-slate-200 bg-white text-slate-800',
};

const TONE_ICON_BG = {
  success: 'bg-emerald-100 text-emerald-700',
  info: 'bg-teal-100 text-teal-700',
  warning: 'bg-amber-100 text-amber-700',
  offline: 'bg-slate-200 text-slate-600',
  neutral: 'bg-slate-100 text-slate-600',
};

const StepTrackerEnhanced = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();

  // Cached-first render: read today's local value synchronously so the
  // page never flashes a spinner over a known value.
  const [steps, setSteps] = useState(() => deviceHealthService.getCachedSteps());
  const [goal, setGoal] = useState(() => user?.daily_step_goal || 10000);
  const [achieved, setAchieved] = useState(false);
  const [customSteps, setCustomSteps] = useState('');
  const [showConfetti, setShowConfetti] = useState(false);

  const [healthStatus, setHealthStatus] = useState(() => deviceHealthService.getStatus());
  const [lastSync, setLastSync] = useState(() => deviceHealthService.lastSyncTime);
  const [online, setOnline] = useState(() => navigator.onLine !== false);
  const [refreshing, setRefreshing] = useState(false);
  const [showPermissionPrompt, setShowPermissionPrompt] = useState(false);
  const [pendingCount, setPendingCount] = useState(() => deviceHealthService.getPending().length);

  const refreshPendingCount = useCallback(() => {
    setPendingCount(deviceHealthService.getPending().length);
  }, []);

  // Lightweight tick that re-renders the "Last synced N mins ago" line.
  const [, setRelTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setRelTick((t) => t + 1), 30000);
    return () => clearInterval(id);
  }, []);

  // Online/offline awareness — single source for the offline banner and
  // for whether we attempt backend writes vs. queue them.
  useEffect(() => {
    const goOnline = () => {
      setOnline(true);
      setHealthStatus(deviceHealthService.getStatus());
      // Flush any queued updates opportunistically.
      flushPendingQueue();
    };
    const goOffline = () => {
      setOnline(false);
      setHealthStatus(deviceHealthService.getStatus());
    };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => {
      window.removeEventListener('online', goOnline);
      window.removeEventListener('offline', goOffline);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // First-time soft prompt: only when permission has never been answered.
  useEffect(() => {
    const status = deviceHealthService.getStatus();
    if (status === STATUS.PERMISSION_NEEDED) {
      setShowPermissionPrompt(true);
    }
  }, []);

  // Fetch goal + today's stored value from backend AFTER initial paint.
  // We always trust the local cache as the immediate display source; the
  // backend value reconciles whichever is higher (so a manual entry made
  // while offline isn't overwritten by a stale 0).
  const fetchProgress = useCallback(async () => {
    if (!online || !user) return;
    try {
      const { data } = await axios.get(`${API}/steps/progress`, { withCredentials: true });
      const remoteSteps = data?.today?.steps ?? 0;
      const remoteGoal = data?.today?.goal || user?.daily_step_goal || 10000;
      setGoal(remoteGoal);
      setAchieved(!!data?.today?.achieved);
      setSteps((local) => {
        const merged = Math.max(local, remoteSteps);
        if (merged !== local) deviceHealthService.setSteps(merged);
        return merged;
      });
      setLastSync(deviceHealthService.lastSyncTime);
      setHealthStatus(deviceHealthService.getStatus());
    } catch (err) {
      // Network/auth issues are non-fatal — local tracking continues.
      try { localStorage.setItem(PENDING_RETRY_KEY, new Date().toISOString()); } catch (_) {}
    }
  }, [online, user]);

  useEffect(() => { fetchProgress(); }, [fetchProgress]);

  const saveToBackend = useCallback(async (nextSteps) => {
    const today = new Date().toISOString().split('T')[0];
    const entry = { date: today, steps: nextSteps, goal };
    if (!online) {
      deviceHealthService.queuePending(entry);
      refreshPendingCount();
      return { queued: true };
    }
    try {
      const { data } = await axios.post(`${API}/steps/track`, entry, { withCredentials: true });
      deviceHealthService.markSynced();
      setLastSync(deviceHealthService.lastSyncTime);
      if (data?.achieved && !achieved) {
        setAchieved(true);
        setShowConfetti(true);
        toast.success('🎉 Goal achieved!');
        setTimeout(() => setShowConfetti(false), 4500);
      }
      return { queued: false, data };
    } catch (err) {
      deviceHealthService.queuePending(entry);
      refreshPendingCount();
      deviceHealthService.pauseSync();
      setHealthStatus(STATUS.SYNC_PAUSED);
      return { queued: true, error: err };
    }
  }, [online, goal, achieved, refreshPendingCount]);

  const flushPendingQueue = useCallback(async () => {
    const queue = deviceHealthService.getPending();
    if (!queue.length) return;
    let allOk = true;
    for (const entry of queue) {
      try {
        await axios.post(`${API}/steps/track`, entry, { withCredentials: true });
      } catch (_) {
        allOk = false;
        break;
      }
    }
    if (allOk) {
      deviceHealthService.clearPending();
      deviceHealthService.markSynced();
      deviceHealthService.resumeSync();
      setLastSync(deviceHealthService.lastSyncTime);
      setHealthStatus(deviceHealthService.getStatus());
      refreshPendingCount();
    }
  }, [refreshPendingCount]);

  const handleConnect = async () => {
    setShowPermissionPrompt(false);
    const result = await deviceHealthService.requestPermission();
    setHealthStatus(deviceHealthService.getStatus());
    if (result.granted) {
      toast.success('Connected. Tracking active.');
    } else {
      toast.message('Health permissions required to auto-track steps. You can still log manually.');
    }
  };

  const handleDecline = () => {
    setShowPermissionPrompt(false);
    // We deliberately do NOT write a 'denied' value here — the user just
    // dismissed the soft prompt, they haven't explicitly declined permission.
    toast.message('You can connect later from this screen.');
  };

  const handleDisconnect = () => {
    if (window.confirm('Disconnect device health tracking? Your logged steps stay on this device.')) {
      deviceHealthService.disconnect();
      setHealthStatus(deviceHealthService.getStatus());
      toast.message('Disconnected. Manual tracking is still available.');
    }
  };

  const handleManualRefresh = async () => {
    setRefreshing(true);
    try {
      // Manual refresh re-reads cached value, attempts backend sync, and
      // flushes any pending offline queue.
      await flushPendingQueue();
      await fetchProgress();
      setSteps((local) => Math.max(local, deviceHealthService.getCachedSteps()));
      if (online) toast.success('Updated successfully');
      else toast.message('Offline — tracking continues locally.');
    } finally {
      // brief delay so the success state is visible
      setTimeout(() => setRefreshing(false), 350);
    }
  };

  const handleManualEntry = async () => {
    const n = parseInt(customSteps, 10);
    if (!Number.isFinite(n) || n <= 0) {
      toast.error('Enter a positive number of steps.');
      return;
    }
    const next = deviceHealthService.setSteps(n);
    setSteps(next);
    setHealthStatus(deviceHealthService.getStatus());
    setCustomSteps('');
    const result = await saveToBackend(next);
    if (result.queued) {
      toast.message('Saved locally. Will sync when you reconnect.');
    } else {
      toast.success('Updated successfully');
    }
  };

  // Derived UI values
  const percentage = Math.min((steps / goal) * 100, 100);
  const remaining = Math.max(goal - steps, 0);
  const presentation = STATUS_PRESENTATION[healthStatus] || STATUS_PRESENTATION[STATUS.UNAVAILABLE];
  const isConnected = healthStatus === STATUS.CONNECTED_ACTIVE || healthStatus === STATUS.CONNECTED_WAITING;
  const isEmpty = steps === 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-emerald-50 pb-24">
      {showConfetti && <Confetti recycle={false} numberOfPieces={420} />}

      <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate(-1)} data-testid="step-tracker-back">
            <ArrowLeft className="h-4 w-4 mr-2" />
            {t('common.back', 'Back')}
          </Button>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Footprints className="h-6 w-6 text-emerald-600" />
            {t('stepTracker.header.title')}
          </h1>
          <div className="w-16" aria-hidden="true" />
        </div>

        {/* Offline banner — top-of-page so it's never missed */}
        {!online && (
          <div
            className="flex items-center gap-3 px-4 py-3 rounded-xl border border-slate-200 bg-slate-50 text-slate-800"
            data-testid="step-tracker-offline-banner"
          >
            <WifiOff className="h-5 w-5 shrink-0 text-slate-500" aria-hidden="true" />
            <div className="text-sm">
              <p className="font-semibold">{t('stepTracker.offline.title')}</p>
              <p className="text-slate-600">{t('stepTracker.offline.body')}</p>
            </div>
          </div>
        )}

        {/* Permission soft prompt */}
        {showPermissionPrompt && (
          <Card className="border-emerald-200 bg-emerald-50 shadow-sm" data-testid="step-tracker-permission-prompt">
            <CardContent className="pt-5">
              <div className="flex items-start gap-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-white text-emerald-700 ring-1 ring-emerald-200">
                  <Smartphone className="h-5 w-5" />
                </span>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 mb-1">{t('stepTracker.permission.title')}</h3>
                  <p className="text-sm text-slate-700 mb-4">
                    {t('stepTracker.permission.body', { platform: deviceHealthService.getPlatformName() })}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button onClick={handleConnect} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="step-tracker-permission-connect">
                      <Smartphone className="h-4 w-4 mr-2" />
                      {t('stepTracker.permission.connect')}
                    </Button>
                    <Button variant="outline" onClick={handleDecline} data-testid="step-tracker-permission-decline">
                      {t('stepTracker.permission.maybeLater')}
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Connection / status card —
            Hidden while the soft permission prompt is showing, otherwise we'd
            stack two "Connect" CTAs on a 320–360px viewport. Once the user
            either taps Maybe later or actually answers the permission prompt
            this card takes over as the persistent status indicator. */}
        {!showPermissionPrompt && (
        <Card className={`shadow-sm ${TONE_CLASSES[presentation.tone] || TONE_CLASSES.neutral}`} data-testid="step-tracker-status-card">
          <CardContent className="pt-4">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${TONE_ICON_BG[presentation.tone] || TONE_ICON_BG.neutral}`}>
                  <presentation.Icon className="h-5 w-5" aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <p className="font-semibold" data-testid="step-tracker-status-label">{presentation.label}</p>
                  <p className="text-sm opacity-90" data-testid="step-tracker-status-sub">{presentation.sub}</p>
                  {lastSync && (
                    <p className="text-xs opacity-80 mt-1 flex items-center gap-1" data-testid="step-tracker-last-sync">
                      <Clock className="h-3 w-3" aria-hidden="true" />
                      {t('stepTracker.sync.lastSynced', { time: formatRelative(lastSync) })}
                    </p>
                  )}
                  {pendingCount > 0 && (
                    <p className="text-xs opacity-80 mt-1" data-testid="step-tracker-pending-count">
                      {pendingCount === 1
                        ? t('stepTracker.sync.queued', { count: pendingCount })
                        : t('stepTracker.sync.queuedPlural', { count: pendingCount })}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 shrink-0">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleManualRefresh}
                  disabled={refreshing}
                  className="bg-white/80"
                  data-testid="step-tracker-refresh-btn"
                  aria-label={t('stepTracker.sync.refreshAria')}
                >
                  <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
                </Button>
                {!isConnected ? (
                  <Button
                    size="sm"
                    onClick={handleConnect}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="step-tracker-connect-btn"
                  >
                    {t('stepTracker.permission.connect')}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={handleDisconnect}
                    className="hover:bg-white/60"
                    data-testid="step-tracker-disconnect-btn"
                  >
                    {t('stepTracker.sync.disconnect')}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Progress card */}
        <Card className="shadow-sm border-emerald-100" data-testid="step-tracker-progress-card">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between text-slate-900">
              <span>{t('stepTracker.progress.title')}</span>
              {percentage >= 100 && <Award className="h-6 w-6 text-amber-500" aria-label={t('stepTracker.progress.goalAriaLabel')} />}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="text-center space-y-2">
              <div
                className="text-5xl font-bold text-emerald-700 tabular-nums"
                data-testid="step-tracker-step-count"
              >
                {steps.toLocaleString()}
              </div>
              <p className="text-slate-500">{t('stepTracker.progress.stepsToday')}</p>
            </div>

            {/* Empty-state guidance — only when truly at zero */}
            {isEmpty && (
              <div
                className="rounded-xl border border-emerald-100 bg-emerald-50/60 px-4 py-3 text-center"
                data-testid="step-tracker-empty-state"
              >
                <div className="flex items-center justify-center mb-1">
                  <span className="relative inline-flex">
                    <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-60 animate-ping" />
                    <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
                  </span>
                </div>
                <p className="font-medium text-slate-800">
                  {healthStatus === STATUS.CONNECTED_WAITING
                    ? t('stepTracker.empty.waiting')
                    : t('stepTracker.empty.startMoving')}
                </p>
                <p className="text-sm text-slate-600 mt-0.5">
                  {t('stepTracker.empty.shortWalkHint')}
                </p>
              </div>
            )}

            {/* Progress bar */}
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">{t('stepTracker.progress.goalLabel', { goal: goal.toLocaleString() })}</span>
                <span className={`font-semibold ${percentage >= 100 ? 'text-emerald-700' : 'text-emerald-600'}`}>
                  {Math.round(percentage)}%
                </span>
              </div>
              <div className="w-full bg-emerald-100 rounded-full h-3 overflow-hidden">
                <div
                  className={`h-full transition-all duration-500 rounded-full ${
                    percentage >= 100
                      ? 'bg-gradient-to-r from-emerald-500 to-teal-500'
                      : 'bg-gradient-to-r from-emerald-400 to-teal-400'
                  }`}
                  style={{ width: `${percentage}%` }}
                  data-testid="step-tracker-progress-fill"
                />
              </div>
              <p className="text-sm text-slate-600 text-center">
                {remaining > 0
                  ? t('stepTracker.progress.stepsToGo', { remaining: remaining.toLocaleString() })
                  : t('stepTracker.progress.goalAchieved')}
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center p-3 bg-emerald-50 rounded-xl">
                <Target className="h-5 w-5 text-emerald-600 mx-auto mb-1" />
                <p className="text-[11px] uppercase tracking-wide text-slate-500">{t('stepTracker.progress.statGoal')}</p>
                <p className="text-lg font-bold text-slate-800">{(goal / 1000).toFixed(0)}k</p>
              </div>
              <div className="text-center p-3 bg-teal-50 rounded-xl">
                <TrendingUp className="h-5 w-5 text-teal-600 mx-auto mb-1" />
                <p className="text-[11px] uppercase tracking-wide text-slate-500">{t('stepTracker.progress.statProgress')}</p>
                <p className="text-lg font-bold text-slate-800">{Math.round(percentage)}%</p>
              </div>
              <div className="text-center p-3 bg-emerald-50 rounded-xl">
                <Activity className="h-5 w-5 text-emerald-600 mx-auto mb-1" />
                <p className="text-[11px] uppercase tracking-wide text-slate-500">{t('stepTracker.progress.statSource')}</p>
                <p className="text-sm font-bold text-slate-800">
                  {isConnected
                    ? (deviceHealthService.hasNativeBridge() ? t('stepTracker.progress.sourceDevice') : t('stepTracker.progress.sourceManual'))
                    : t('stepTracker.progress.sourceManual')}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Manual log — always available, not gated on connection */}
        <Card className="shadow-sm border-emerald-100" data-testid="step-tracker-manual-card">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-slate-800">{t('stepTracker.manual.title')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex gap-2">
              <Input
                type="number"
                inputMode="numeric"
                placeholder={t('stepTracker.manual.placeholder')}
                value={customSteps}
                onChange={(e) => setCustomSteps(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleManualEntry(); }}
                data-testid="step-tracker-manual-input"
                min="0"
              />
              <Button
                onClick={handleManualEntry}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="step-tracker-manual-submit"
              >
                {t('stepTracker.manual.submit')}
              </Button>
            </div>
            <p className="text-xs text-slate-500">
              {t('stepTracker.manual.footer')}
            </p>
          </CardContent>
        </Card>

        {/* Trust & privacy reassurance */}
        <Card
          className="border-emerald-100 bg-emerald-50/40"
          data-testid="step-tracker-trust-card"
        >
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" aria-hidden="true" />
              <div className="text-sm text-slate-700">
                <p className="font-medium text-slate-800 mb-1">{t('stepTracker.trust.title')}</p>
                <ul className="space-y-1 text-slate-600">
                  <li>• {t('stepTracker.trust.itemData')}</li>
                  <li>• {t('stepTracker.trust.itemOffline')}</li>
                  <li>• {t('stepTracker.trust.itemNoInvent')}</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Generic explanatory card kept for parity with the previous layout */}
        <Card className="border-amber-100 bg-amber-50/60" data-testid="step-tracker-how-it-works">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
              <div className="text-sm text-amber-900">
                <p className="font-semibold mb-1">{t('stepTracker.howItWorks.title')}</p>
                <ul className="list-disc list-inside space-y-1 text-amber-800">
                  <li>{t('stepTracker.howItWorks.itemConnect')}</li>
                  <li>{t('stepTracker.howItWorks.itemManual')}</li>
                  <li>{t('stepTracker.howItWorks.itemOfflineQueue')}</li>
                  <li>{t('stepTracker.howItWorks.itemDisconnect')}</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StepTrackerEnhanced;
